import { QueryFunction, QueryKey } from "@tanstack/react-query";
import axios, { CanceledError } from "axios";
import { StatusCodes } from "http-status-codes";
import { AddChatMessageRequest, ChatHistoryExport, ChatMessage, Doc, File, Link, LinkBulkImport, LoginResponse, Model, ModelPrice, Product, RequestLoginRequest, RequestLoginResponse, Settings, Skill, VerifyOtpRequest } from "../types";
import { PANE_ID } from "../state/ai/pane";
import { Cookies, getCookie } from "../util/cookies";
import { RouteParam, ROUTES } from "./api.routes";

export const API_URL = window?.location?.hostname === "localhost" ? "http://localhost:8080/v1" : "https://api.mfaunderwriting.com/v1";

type METHOD = "GET" | "POST" | "DELETE";

interface IDispatchParams<T = Object> {
  body?: T,
  showMessage?: boolean,
  showMessageOnSuccess?: boolean,
  abortController?: AbortController | null,
  abortSignal?: AbortSignal | null
}

export type ApiResponse<T = any> = {
  success: false,
  canceled: boolean,
  message: string
} | {
  success: true,
  data: T
}


const createApiError = (showMessage: boolean, message: string = "An error occured"): ApiResponse => {
  return {
    success: false,
    canceled: false,
    message
  };
}

const createCanceledResponse = (): ApiResponse => ({
  canceled: true,
  success: false,
  message: "Cancelled."
});

const createApiSuccessPayload = <T>(payload: T): ApiResponse<T> => {
  return ({
    success: true,
    data: payload
  });
}

const apiRequest = async<RequestT = any, ResponseT = any>(method: METHOD, url = "/", { abortController, abortSignal, body, showMessage = true }: IDispatchParams<RequestT> = {}): Promise<ApiResponse<ResponseT>> => {
  try {
    const res = await axios({
      baseURL: API_URL,
      method,
      url,
      headers: {
        'Authorization': `Bearer ${getCookie(Cookies.Session)}`,
        'Accept': 'application/json'
      },
      signal: abortController?.signal ?? abortSignal ?? undefined,
      data: body
    });

    if (!res) return createApiError(showMessage);
    if (res.status >= StatusCodes.OK && res.status < StatusCodes.MULTIPLE_CHOICES) return createApiSuccessPayload<ResponseT>(res.data);

    return createApiError(showMessage, res.data?.message || "Something went wrong.");
  }
  catch (err: any) {
    if (err instanceof CanceledError) return createCanceledResponse();
    if (err?.response?.data?.message) return createApiError(showMessage, err.response.data?.message);
  }

  return createApiError(showMessage);
}

export const streamApiRequest = async <RequestT = any, ResponseT = any>(
  method: METHOD,
  url = "/",
  { body, showMessage = true, abortController, abortSignal }: IDispatchParams<RequestT> = {},
  onEvent?: (event: Record<string, any>) => Promise<void>
): Promise<ApiResponse<ResponseT>> => {
  const signal = abortController?.signal ?? abortSignal ?? undefined;

  try {

    const response = await fetch(`${API_URL}${url}`, {
      method,
      headers: {
        'Authorization': `Bearer ${getCookie(Cookies.Session)}`,
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify(body),
      signal
    });

    if (!response.ok) return createApiError(showMessage, "Request failed");

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let finalRawData = "";
    let leftover = "";
    let foundJson = false;

    while (true) {
      if (signal?.aborted) {
        await reader!.cancel();
        return createCanceledResponse();
      }

      const { done, value } = await reader!.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = (leftover + chunk).split("\n");
      leftover = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        if (trimmed.startsWith("error: ")) {
          return createApiError(showMessage, trimmed.replace("error: ", ""));
        }

        if (trimmed.startsWith("json: ")) {
          finalRawData = trimmed.replace("json: ", "").trim();
          foundJson = true;
          break;
        }

        if (trimmed.startsWith("data: ")) {
          const raw = trimmed.replace("data: ", "").trim();
          if (raw === "[DONE]") continue;
          try {
            const parsedEvent = JSON.parse(raw);
            if (onEvent && parsedEvent) {
              await onEvent(parsedEvent);
            }
          } catch {
          }
        }
      }
      if (foundJson) break;
    }

    if (!foundJson && leftover.trim().startsWith("json: ")) {
      finalRawData = leftover.trim().replace("json: ", "").trim();
    }

    if (!finalRawData) return createApiError(showMessage, "AI ran out of juice.");

    return createApiSuccessPayload<ResponseT>(JSON.parse(finalRawData));
  } catch (err: any) {
    if (err?.name === "AbortError" || signal?.aborted) return createCanceledResponse();
    return createApiError(showMessage, err.message || "Something went wrong!");
  }
};

export const tanStackFetch: QueryFunction<unknown, QueryKey, never> = async ({ queryKey, signal }) => {
  try {

    if (!queryKey) {
      return null;
    }

    let ep = "/";

    if (typeof queryKey === "string") ep = queryKey;
    else if (Array.isArray(queryKey)) ep = queryKey.join("/");
    else ep = queryKey.toString();

    if (!ep) return null;

    const res = await apiRequest("GET", ep, { abortSignal: signal });

    if (!res.success) return null;
    return res.data || true;
  }
  catch (e) {
    throw e;
  }
}


const request = <ReqT = void, ResT = void>(method: METHOD, ep: string | ((b: ReqT) => string)) => (
  async (body: ReqT, cfg: Omit<IDispatchParams<ReqT>, "body"> = {}) => await apiRequest<ReqT, ResT>(
    method,
    typeof ep === "string" ? ep : ep(body),
    { ...cfg, body: method === "POST" ? body : undefined }
  )
);

const streamRequest = <ReqT = void, ResT = void>(method: METHOD, ep: string | ((b: ReqT) => string)) => (
  async (
    body: ReqT,
    cfg: Omit<IDispatchParams<ReqT>, "body"> = {},
    onEvent?: (event: Record<string, any>) => Promise<void>
  ) => await streamApiRequest<ReqT, ResT>(
    method,
    typeof ep === "string" ? ep : ep(body),
    { ...cfg, body },
    onEvent
  )
);

export const usersRequestLogin = request<RequestLoginRequest, RequestLoginResponse>("POST", ROUTES.Auth.Login);
export const usersVerifyLogin = request<VerifyOtpRequest, LoginResponse>("POST", ROUTES.Auth.Verify);
export const usersLogOut = request("GET", ROUTES.Auth.Logout);

export const favoriteLinksAdd = request<string>("GET", f => ROUTES.Links.Favorites.ById.getValue({ [RouteParam.Id]: f }));
export const favoriteLinksRemove = request<string>("DELETE", f => ROUTES.Links.Favorites.ById.getValue({ [RouteParam.Id]: f }));

export const filesUpdate = request<Doc<File>>("POST", b => ROUTES.Files.ById.getValue({ [RouteParam.Id]: b._id }));
export const filesCreate = request<File>("POST", ROUTES.Files.All);
export const filesDownload = request<string, File>("GET", f => ROUTES.Files.ById.getValue({ [RouteParam.Id]: f }));
export const filesDelete = request<string>("DELETE", f => ROUTES.Files.ById.getValue({ [RouteParam.Id]: f }));

export const linksUpdate = request<Doc<Link>>("POST", b => ROUTES.Links.ById.getValue({ [RouteParam.Id]: b._id }));
export const linksCreate = request<Link>("POST", ROUTES.Links.All);
export const linksBulkImport = request<LinkBulkImport>("POST", ROUTES.Links.Bulk);
export const linksDelete = request<string>("DELETE", l => ROUTES.Links.ById.getValue({ [RouteParam.Id]: l }));

export const productsUpdate = request<Doc<Product>>("POST", b => ROUTES.Products.ById.getValue({ [RouteParam.Id]: b._id }));
export const productsCreate = request<Product>("POST", ROUTES.Products.All);
export const productsDelete = request<string>("DELETE", p => ROUTES.Products.ById.getValue({ [RouteParam.Id]: p }));

export const aiAddMessage = streamRequest<AddChatMessageRequest, ChatMessage>("POST", ROUTES.Ai.route);
export const aiClearHistory = request<{ skill: string }>("DELETE", params => ROUTES.Ai.query({ ...params, pane: PANE_ID }));
export const aiExportChatHistory = request<{ skill: string }, ChatHistoryExport>("GET", params => ROUTES.ChatHistoryExport.query({ ...params, pane: PANE_ID }));

export const skillsCreate = request<Skill>("POST", ROUTES.Skills.All.route);
export const skillsUpdate = request<Doc<Skill>>("POST", x => ROUTES.Skills.ById.getValue({ [RouteParam.Id]: x._id }));
export const skillsDelete = request<string>("DELETE", x => ROUTES.Skills.ById.getValue({ [RouteParam.Id]: x }));

export const modelsCreate = request<Model>("POST", ROUTES.Models.All.route);
export const modelsUpdate = request<Doc<Model>>("POST", x => ROUTES.Models.ById.getValue({ [RouteParam.Id]: x._id }));
export const modelsDelete = request<string>("DELETE", x => ROUTES.Models.ById.getValue({ [RouteParam.Id]: x }));

export const modelPricesCreate = request<ModelPrice>("POST", ROUTES.ModelPrices.All.route);
export const modelPricesUpdate = request<Doc<ModelPrice>>("POST", x => ROUTES.ModelPrices.ById.getValue({ [RouteParam.Id]: x._id }));
export const modelPricesDelete = request<string>("DELETE", x => ROUTES.ModelPrices.ById.getValue({ [RouteParam.Id]: x }));

export const settingsUpdate = request<Settings>("POST", ROUTES.Settings);

export const aiToolsDownloadContainerFile = request<string, string>("GET", b => ROUTES.AiTools.ContainerFileDownload.getValue({ [RouteParam.Id]: b }));
